import React, { useContext } from "react";
import PropTypes from "prop-types";
import { ThreeDots } from "react-loader-spinner";
import AuthContext from "connections/auth/AuthProvider";

// ######### MATERIAL UI
import { makeStyles, Typography, Box } from "@material-ui/core";

// ######### COMPONENTS
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import Table from "components/Table/Table.js";

// ######### STYLES
import { grayColor } from "assets/jss/material-dashboard-pro-react.js";
import { useTranslation } from "react-i18next";

const tablePreviewStyle = (theme) => ({
  card: {
    flex: 1,
    minWidth: "400px",
    height: "100%",
    maxHeight: "100%",
    display: "flex",
    flexDirection: "column",
    marginTop: theme.spacing(0),
    overflow: "hidden",
  },
  cardBody: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    padding: theme.spacing(2),
  },
  tableContainer: {
    flex: 1,
    height: "100%",
    overflow: "auto",
    minHeight: "200px",
    position: "relative",
    // Force the table responsive wrapper to not interfere
    "& > div": {
      height: "100%",
      overflow: "visible !important",
    },
    // Target the table wrapper that might be created by the Table component
    "& .tableResponsive": {
      overflow: "visible !important",
      height: "100%",
    },
    // Target the actual table elements
    "& table": {
      position: "relative",
      borderCollapse: "separate !important",
    },
    "& thead": {
      position: "sticky !important",
      top: "0px !important",
      zIndex: "1000 !important",
      backgroundColor: "white !important",
    },
    "& thead tr": {
      position: "sticky !important",
      top: "0px !important",
      backgroundColor: "white !important",
      zIndex: "1000 !important",
    },
    "& thead th": {
      position: "sticky !important",
      top: "0px !important",
      backgroundColor: `${grayColor[10]} !important`,
      fontWeight: "500 !important",
      fontSize: "0.875rem !important",
      borderBottom: `2px solid ${grayColor[5]} !important`,
      borderRight: `1px solid ${grayColor[8]} !important`,
      zIndex: "1000 !important",
      "&:last-child": {
        borderRight: "none !important",
      },
    },
    // Material-UI classes
    "& .MuiTableHead-root": {
      position: "sticky !important",
      top: "0px !important",
      zIndex: "1000 !important",
      backgroundColor: "white !important",
    },
    "& .MuiTableHead-root .MuiTableRow-root": {
      position: "sticky !important",
      top: "0px !important",
      backgroundColor: "white !important",
      zIndex: "1000 !important",
    },
    "& .MuiTableHead-root .MuiTableCell-root": {
      position: "sticky !important",
      top: "0px !important",
      backgroundColor: `${grayColor[10]} !important`,
      fontWeight: "500 !important",
      fontSize: "0.875rem !important",
      borderBottom: `2px solid ${grayColor[5]} !important`,
      borderRight: `1px solid ${grayColor[8]} !important`,
      zIndex: "1000 !important",
      "&:last-child": {
        borderRight: "none !important",
      },
    },
    // Body cell borders
    "& tbody td, & .MuiTableBody-root .MuiTableCell-root": {
      borderRight: `1px solid ${grayColor[8]} !important`,
      "&:last-child": {
        borderRight: "none !important",
      },
    },
  },
  message: {
    fontSize: "0.875rem",
    color: grayColor[1],
  },
  noInputMessage: {
    fontStyle: "italic",
    color: grayColor[1],
    textAlign: "center",
    padding: theme.spacing(4),
  },
  loadingBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "200px",
  },
  errorMessage: {
    color: theme.palette.error.main,
    textAlign: "center",
    padding: theme.spacing(2),
  },
});

export default function TabTablePreview({ tableData, isLoading, error }) {
  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails.implementation_color;
  const useStyles = makeStyles(tablePreviewStyle);
  const classes = useStyles();

  const T_PREFIX = "rgm.inputs.table_preview";
  const { t } = useTranslation("translation", { keyPrefix: T_PREFIX });

  return (
    <Card className={classes.card}>
      <CardBody className={classes.cardBody}>
        {isLoading ? (
          <Box className={classes.loadingBox}>
            <ThreeDots
              height="80"
              width="80"
              radius="9"
              color={clientColor}
              ariaLabel="three-dots-loading"
              wrapperStyle={{}}
              wrapperClassName=""
              visible={true}
            />
          </Box>
        ) : error ? (
          <Typography className={classes.errorMessage}>
            {t("error")}: {error}
          </Typography>
        ) : !tableData || !tableData.rows || tableData.rows.length === 0 ? (
          <Typography className={classes.noInputMessage}>
            {t("no_data_available")}
          </Typography>
        ) : (
          <Box className={classes.tableContainer}>
            <Table
              tableHead={tableData.headers}
              tableData={tableData.rows}
              hover={true}
              striped={true}
              tableHeaderColor="gray"
            />
          </Box>
        )}
      </CardBody>
    </Card>
  );
}

TabTablePreview.propTypes = {
  tableData: PropTypes.shape({
    headers: PropTypes.arrayOf(PropTypes.string),
    rows: PropTypes.arrayOf(PropTypes.array),
  }),
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  onRefresh: PropTypes.func.isRequired,
  variants: PropTypes.array.isRequired,
  selectedVariant: PropTypes.number,
  setSelectedVariant: PropTypes.func.isRequired,
};
